import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { API, DONATION_OPTIONS, MONTHLY_OPTIONS } from "@/lib/content";
import { errorText } from "@/lib/errors";
import { Reveal } from "./Reveal";

const pill = (active) =>
  `rounded-full border border-white px-6 py-3 transition-colors ${active ? "bg-white/20 ring-2 ring-white" : "hover:bg-white/10"}`;

export const Donate = () => {
  const [mode, setMode] = useState("once");
  const [choice, setChoice] = useState("donate_50");
  const [custom, setCustom] = useState("");
  const [loading, setLoading] = useState(false);
  const options = mode === "once" ? DONATION_OPTIONS : MONTHLY_OPTIONS;

  const switchMode = (m) => {
    setMode(m);
    setChoice(m === "once" ? "donate_50" : "monthly_25");
  };

  const give = async () => {
    if (choice === "monthly_custom" && !(Number(custom) >= 5)) {
      toast.error("Please enter a monthly amount of at least $5.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: choice,
        origin_url: window.location.origin,
        ...(choice === "monthly_custom" ? { amount: Number(custom) } : {}),
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(errorText(err));
      setLoading(false);
    }
  };

  return (
    <section id="donate" className="bg-brass text-white py-24 md:py-32 scroll-mt-20" data-testid="donate-section">
      <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-[1.2fr_1fr] gap-12 items-center">
        <Reveal className="min-w-0">
          <h2 className="dp">Give so another couple <em>can be helped</em></h2>
          <p className="mt-4 max-w-lg text-white/90">Donations are more than welcome and sustain this nonprofit's overhead.</p>
          <p className="mt-4 max-w-lg text-white/90">A monthly gift keeps the lights on steadily, so couples can count on us.</p>
        </Reveal>
        <Reveal delay={120}>
          <div className="inline-flex rounded-full bg-white/15 p-1 mb-6" role="tablist" aria-label="Gift frequency">
            {[["once", "One-time"], ["monthly", "Monthly"]].map(([m, label]) => (
              <button key={m} type="button" role="tab" aria-selected={mode === m} onClick={() => switchMode(m)}
                className={`rounded-full px-5 py-2 text-sm transition-colors ${mode === m ? "bg-white text-pine" : "text-white"}`}
                data-testid={`donate-mode-${m}`}>
                {label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 tn" role="radiogroup" aria-label="Donation amount">
            {options.map((o) => (
              <button key={o.key} type="button" role="radio" aria-checked={choice === o.key} onClick={() => setChoice(o.key)}
                className={pill(choice === o.key)} data-testid={`donate-amount-${o.key}`}>
                {o.label}
              </button>
            ))}
          </div>
          {choice === "monthly_custom" && (
            <label className="mt-5 flex items-center gap-2 border-b border-white/70 max-w-[220px] py-2">
              <span>$</span>
              <input type="number" min="5" max="10000" step="1" value={custom} onChange={(e) => setCustom(e.target.value)}
                placeholder="Amount" className="w-full bg-transparent placeholder:text-white/60 focus:outline-none tn" data-testid="donate-monthly-custom-input" />
              <span className="text-sm text-white/80">/month</span>
            </label>
          )}
          <button type="button" onClick={give} disabled={loading}
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-white text-pine px-8 py-3 transition-transform hover:-translate-y-0.5 disabled:opacity-70"
            data-testid="donate-submit-button">
            {loading && <Loader2 size={16} className="animate-spin" />}
            {mode === "monthly" ? "Give monthly" : "Donate"}
          </button>
          <p className="mt-4 text-sm text-white/80">
            {mode === "monthly" ? "Secure checkout. Your gift repeats once a month." : 'Secure checkout. "Other amount" lets you choose any gift from $5.'}
          </p>
        </Reveal>
      </div>
    </section>
  );
};
